import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Creates a pinned timeline for a single section and animates its cards.
 * Isolated for easy debugging and modular importing.
 *
 * @param {HTMLElement} section - The section element to pin.
 */
export const createPinnedCardTimeline = (section) => {
   const cards = gsap.utils.toArray(section.querySelectorAll('[data-card]'));

   // Need at least 2 cards to create a transition sequence
   if (cards.length < 2) {
      console.warn(
         '[ScrollTrigger] Not enough cards found to animate in:',
         section,
      );
      return;
   }

   const peekWidth = 20;

   const mm = gsap.matchMedia();

   mm.add('(min-width: 992px)', () => {
      // Create the master timeline bound to the scrollbar
      const tl = gsap.timeline({
         scrollTrigger: {
            trigger: section,
            pin: true,
            start: 'top top', // Pins when the top of the section hits the top of the viewport
            // Dynamically calculate scroll duration based on card count (100% viewport height per card)
            end: '+=150%',
            scrub: 1, // 1-second smoothing lag so the translation doesn't feel rigid
            invalidateOnRefresh: true, // Recalculates on window resize
            // markers: true, // Uncomment to debug visual start/end triggers
         },
      });

      // Animate cards sequentially.
      // We skip index 0 so Card 1 sits natively as the base layer,
      // while Cards 2 and 3 translate over it.
      cards.forEach((card, index) => {
         if (index === 0) return;

         let yValue = 20 * index;

         tl.fromTo(
            card,
            {
               x: () =>
                  cards[0].offsetLeft + index * peekWidth - card.offsetLeft, // Optional: scale slightly down while stacked for a 3D depth effect
               // scale: 0.9,
               y: yValue,
            },
            {
               x: 0, // Translate out to their native CSS Grid column
               scale: 1,
               y: 0,
               ease: 'none',
               duration: 1,
            },
            '<',
         );
      });
   });

   return mm;
};

/**
 * NEW: Creates a pinned horizontal slide sequence.
 * Uses the exact same matchMedia and pinning architecture as your cards.
 *
 * @param {HTMLElement} section - The section element to pin.
 */
export const createPinnedHorizontalTimeline = (section) => {
   const cards = gsap.utils.toArray(section.querySelectorAll('[data-card]'));

   if (cards.length < 2) {
      console.warn(
         '[ScrollTrigger] Not enough cards found to horizontally unstack:',
         section,
      );
      return;
   }

   const mm = gsap.matchMedia();

   mm.add('(min-width: 992px)', () => {
      const tl = gsap.timeline({
         scrollTrigger: {
            trigger: section,
            pin: true,
            start: 'top top',
            // Assigns 100% viewport height of scroll per sequential step
            // end: () => `+=${(cards.length - 1) * 100}%`,
            end: () => `+=${(cards.length - 1) * 50}%`,
            scrub: 1,
            invalidateOnRefresh: true,
         },
      });

      // Loop through each sequential movement step (Step 1 to N-1)
      for (let step = 1; step < cards.length; step++) {
         // Get all cards that need to move during this step (from current step index to the end)
         const movingCards = cards.slice(step);

         movingCards.forEach((card, index) => {
            // Calculate pixel offset of the previous grid slot (where the card currently sits)
            const fromX = () => cards[step - 1].offsetLeft - card.offsetLeft;
            // Calculate pixel offset of the destination slot (where the lead card of this step locks in)
            const toX = () => cards[step].offsetLeft - card.offsetLeft;

            // The lead card of a step starts sequentially AFTER the previous step finishes.
            // Trailing cards use '<' so they move simultaneously with the lead card!
            const positionParam = index === 0 ? undefined : '<';

            tl.fromTo(
               card,
               { x: fromX },
               {
                  x: toX,
                  ease: 'none',
                  duration: 1,
               },
               positionParam,
            );
         });
      }
   });

   return mm;
};

/**
 * Animation Strategy Registry
 * Maps the data-pin-section attribute value directly to its builder function.
 */
const pinStrategies = {
   cards: createPinnedCardTimeline,
   horizontal: createPinnedHorizontalTimeline,
   // Fallback to cards if attribute is left empty: data-pin-section=""
   default: createPinnedCardTimeline,
};

/**
 * Scans the DOM and initializes all pinned card sections based on their assigned strategy.
 *
 * @param {string} [selector='[data-pin-section]'] - CSS selector for target sections.
 */
export const initPinnedCards = (selector = '[data-pin-section]') => {
   const sections = document.querySelectorAll(selector);

   if (!sections.length) return;

   sections.forEach((section) => {
      const strategyName =
         section.getAttribute('data-pin-section') || 'default';
      const initTimeline =
         pinStrategies[strategyName] || pinStrategies['default'];

      initTimeline(section);
   });
};
